import React from "react";
import { Trash2Icon } from "lucide-react";
import { RecordBadge, CompanySVG } from "../../component/picker-shared";

export type CompanyRow = {
  id: string | number;
  name: string;
  avatarUrl?: string;
  isPrimary?: boolean;
  location?: string;
};

function Row({
  item,
  onOpen,
  onSetPrimary,
  onUnlink,
}: {
  item: CompanyRow;
  onOpen?: (id: string | number) => void;
  onSetPrimary?: (id: string | number) => void;
  onUnlink?: (id: string | number) => void;
}) {
  return (
    <div className="group flex items-center py-2 gap-2">
      {/* Name & Avatar */}
      <div className="flex items-center gap-2 min-w-0 w-96">
        {item.avatarUrl ? (
          <img src={item.avatarUrl} alt="" className="w-5 h-5 rounded-full shrink-0" />
        ) : (
          <RecordBadge Icon={CompanySVG} shape="square" className="w-5 h-5" iconScale={1.6} />
        )}
        <div
          className="truncate text-sm font-medium text-gray-900 cursor-pointer hover:underline"
          title={item.name}
          onClick={() => onOpen?.(item.id)}
        >
          {item.name}
        </div>
        {item.isPrimary && (
          <span className="inline-flex items-center rounded-full border border-blue-500 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
            Primary
          </span>
        )}
      </div>

      {/* Secondary Info (Location) */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {item.location && <div className="truncate text-xs text-gray-700">{item.location}</div>}
      </div>

      {/* Actions (show on hover) */}
      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100">
        {onSetPrimary && (
          <button
            type="button"
            onClick={() => onSetPrimary(item.id)}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Set as Primary"
            title="Set as Primary"
            disabled={item.isPrimary}
          >
            <svg
              className="w-4 h-4 text-gray-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
        )}
        <button
          type="button"
          onClick={() => onUnlink?.(item.id)}
          className="p-1 rounded hover:bg-gray-100"
          aria-label="Unlink"
          title="Unlink"
        >
          <Trash2Icon className="w-4 h-4 text-red-500" />
        </button>
      </div>
    </div>
  );
}

interface CompaniesSectionProps {
  title: string;
  action: React.ReactNode;
  items: CompanyRow[];
  onOpen?: (id: string | number) => void;
  onSetPrimary?: (id: string | number) => void;
  onUnlink?: (id: string | number) => void;
}

export default function CompaniesSection({
  title,
  action,
  items,
  onOpen,
  onSetPrimary,
  onUnlink,
}: CompaniesSectionProps) {
  return (
    <div className="w-full flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between pb-2 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {action}
      </div>

      {/* List */}
      <div className="flex flex-col divide-y divide-gray-100">
        {items.map((item) => (
          <Row
            key={item.id}
            item={item}
            onOpen={onOpen}
            onSetPrimary={onSetPrimary}
            onUnlink={onUnlink}
          />
        ))}
        {items.length === 0 && (
          <div className="py-8 text-center text-sm text-gray-600">No items linked yet</div>
        )}
      </div>
    </div>
  );
}
